import { GridApi } from 'ag-grid-community';
import { useCallback, useEffect, useRef } from 'react';
import { Action, ObjectType, SocketData } from 'api/pushes';
import { TaskAPI } from 'api/tasks';
import { EXTRA_DETAILS } from 'constant';
import { useMultiProjectWsUpdatesAllTasks } from 'hooks/use-multi-project-ws-updates-updated';
import { useProjectPlaceholderWebsocket } from 'hooks/websockets';
import { TaskField } from 'stores/fields';
import { Task } from 'stores/tasks';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { unreachable } from 'utils/unreachable';
import { constructNormalizedTask } from './common';
import { useFilterEffect } from './filters';

type TaskSocketData = Pick<Task, 'taskId'>;

interface Params {
  fields: Record<number, TaskField>;
  gridApi?: GridApi;
  isEnabled: boolean;
}
// TODO: chandra refactor for scaling.

const getTasKeyDiff = (from?: Task, to?: Task): string[] => {
  const mergedTask = { ...from, ...to };
  const isPrimitive = (val) => Object(val) !== val;
  const isSame = (task1, task2, key) => task1?.[key] === task2?.[key];
  const hasDifferentValues = (key: string): boolean =>
    isPrimitive(from?.[key]) && !isSame(from, to, key);
  const getCorrectedColId = (colName) =>
    colName === 'taskName' ? 'status' : colName;

  const keys = Object.keys(mergedTask) as unknown as string[];

  return keys.filter(hasDifferentValues).map(getCorrectedColId);
};

export const useAllTasksPaginatedSocketUpdates = ({
  fields,
  gridApi,
  isEnabled,
}: Params): ((newProjectIds: number[]) => void) => {
  const gridApiRef = useRef<GridApi>();
  const fieldsRef = useRef<Record<number, TaskField>>();
  const projectsSubscribedRef = useRef<number[]>([]);

  useEffect(() => {
    gridApiRef.current = gridApi;
  }, [gridApi]);

  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  useFilterEffect(() => {
    projectsSubscribedRef.current = [];
  });

  const handler = useCallback(
    async ({
      data,
      action,
      projectId,
    }: SocketData<TaskSocketData | number[]>) => {
      if (!isEnabled) {
        return;
      }
      if (Array.isArray(data)) {
        return;
      }

      switch (action) {
        case Action.Update: {
          const rowNode = gridApiRef.current?.getRowNode(`${data.taskId}`);

          if (!rowNode) {
            return;
          }
          const task = await TaskAPI.getTask(projectId, data.taskId);
          const updatedTask = {
            ...task,
            fields: task.fields.map(withValidCustomFieldsForData),
          };
          const { byId: newLookup } = constructNormalizedTask({
            fieldList: fieldsRef.current as Record<number, TaskField>,
            isLoading: false,
            taskList: [updatedTask],
          });
          const newTask = newLookup[data.taskId][1];

          gridApiRef.current?.flashCells({
            rowNodes: [rowNode],
            columns: getTasKeyDiff(newTask, rowNode.data),
          });
          rowNode.setData({
            ...newTask,
            [EXTRA_DETAILS]: rowNode.data[EXTRA_DETAILS],
          });

          return;
        }
        case Action.Delete:
          // TODO: right now grid purely supports server side retrival. So have to check with any one method.

          return;
        default:
          unreachable(action as never);
      }
    },
    [isEnabled],
  );
  const updateSocketSubscriber = useMultiProjectWsUpdatesAllTasks(handler, [
    Action.Update,
    ObjectType.Task,
  ]);

  const deleteSocketSubscriber = useMultiProjectWsUpdatesAllTasks(handler, [
    Action.Delete,
    ObjectType.Task,
  ]);
  const socketSubscriber = useCallback(
    (newProjectIds: number[]) => {
      const newProjectsToSubscribe = newProjectIds.filter(
        (id) => !projectsSubscribedRef.current.includes(id),
      );

      updateSocketSubscriber(newProjectsToSubscribe);
      deleteSocketSubscriber(newProjectsToSubscribe);
      projectsSubscribedRef.current?.push(...newProjectsToSubscribe);
    },
    [deleteSocketSubscriber, updateSocketSubscriber],
  );

  useProjectPlaceholderWebsocket();

  return socketSubscriber;
};
